import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/hooks/useAuth";
import { getAllProfiles, updateProfile, adjustBalance, validateMoneyInput, toMoney } from "@/services/supabaseService";
import type { Profile } from "@/lib/supabase";
import { DISTRIBUTOR_LEVELS } from "@/mocks/users";
import AdminLayout from "@/components/feature/AdminLayout";
import { supabase } from "@/lib/supabase";

const LEVEL_STYLES: Record<string, string> = {
  "普通分銷": "bg-slate-100 text-slate-700",
  "核心分銷": "bg-amber-100 text-amber-700",
  "特約經銷": "bg-emerald-100 text-emerald-700",
  "管理員": "bg-stone-100 text-stone-600",
};

export default function MemberManage() {
  const navigate = useNavigate();
  const { user, isAdmin } = useAuth();
  const [users, setUsers] = useState<Profile[]>([]);
  const [search, setSearch] = useState("");
  const [filterLevel, setFilterLevel] = useState<string>("all");
  const [isLoading, setIsLoading] = useState(true);
  const [loadError, setLoadError] = useState("");

  // Member edit modal state
  const [editModal, setEditModal] = useState<Profile | null>(null);
  const [editLevel, setEditLevel] = useState<string>("");
  const [editPhone, setEditPhone] = useState("");
  const [editNotes, setEditNotes] = useState("");
  const [saveStatus, setSaveStatus] = useState<"idle" | "saving" | "success" | "error">("idle");

  // Total orders edit state inside modal
  const [editTotalOrders, setEditTotalOrders] = useState("");
  const [savingTotalOrders, setSavingTotalOrders] = useState(false);

  // Balance adjustment sub-state inside modal
  const [adjustAmount, setAdjustAmount] = useState("");
  const [adjustNote, setAdjustNote] = useState("");
  const [adjustType, setAdjustType] = useState<"add" | "subtract">("add");
  const [adjustError, setAdjustError] = useState("");
  const [adjustSaving, setAdjustSaving] = useState(false);

  // Password reset state inside modal
  const [resetPwd, setResetPwd] = useState("");
  const [resetConfirmPwd, setResetConfirmPwd] = useState("");
  const [resetError, setResetError] = useState("");
  const [resetSaving, setResetSaving] = useState(false);
  const [resetSuccess, setResetSuccess] = useState(false);

  // Email change state inside modal
  const [newEmail, setNewEmail] = useState("");
  const [confirmNewEmail, setConfirmNewEmail] = useState("");
  const [emailError, setEmailError] = useState("");
  const [emailSaving, setEmailSaving] = useState(false);
  const [emailSuccess, setEmailSuccess] = useState(false);

  const loadData = async () => {
    setIsLoading(true);
    setLoadError("");
    try {
      const { data, error } = await supabase
        .from("profiles")
        .select("*")
        .order("created_at", { ascending: false });
      if (error) throw error;
      setUsers((data as Profile[]) || []);
    } catch (err: unknown) {
      const msg = err instanceof Error ? err.message : "讀取會員資料失敗";
      setLoadError(msg);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    loadData();
  }, []);

  const openEditModal = (member: Profile) => {
    setEditModal(member);
    setEditLevel(member.level);
    setEditPhone(member.phone || "");
    setEditNotes(member.notes || "");
    setEditTotalOrders(String(member.total_orders ?? 0));
    setSaveStatus("idle");
    setAdjustAmount("");
    setAdjustNote("");
    setAdjustType("add");
    setAdjustError("");
    setNewEmail("");
    setConfirmNewEmail("");
    setEmailError("");
    setEmailSuccess(false);
    setResetPwd("");
    setResetConfirmPwd("");
    setResetError("");
    setResetSaving(false);
    setResetSuccess(false);
  };

  const closeEditModal = () => {
    setEditModal(null);
    setEditLevel("");
    setEditPhone("");
    setEditNotes("");
    setEditTotalOrders("");
    setSaveStatus("idle");
    setAdjustAmount("");
    setAdjustNote("");
    setAdjustType("add");
    setAdjustError("");
    setResetPwd("");
    setResetConfirmPwd("");
    setResetError("");
    setResetSaving(false);
    setResetSuccess(false);
    setNewEmail("");
    setConfirmNewEmail("");
    setEmailError("");
    setEmailSaving(false);
    setEmailSuccess(false);
  };

  const handleSaveProfile = async () => {
    if (!editModal) return;
    setSaveStatus("saving");
    try {
      await updateProfile(editModal.id, {
        level: editLevel,
        phone: editPhone.trim(),
        notes: editNotes.trim() || null,
      });
      setSaveStatus("success");
      await loadData();
      setTimeout(() => setSaveStatus("idle"), 1500);
    } catch {
      setSaveStatus("error");
    }
  };

  const handleSaveTotalOrders = async () => {
    if (!editModal) return;
    const val = parseInt(editTotalOrders.trim(), 10);
    if (Number.isNaN(val) || val < 0) {
      alert("請輸入有效的非負整數");
      return;
    }
    setSavingTotalOrders(true);
    try {
      await updateProfile(editModal.id, { total_orders: val });
      setEditModal((prev) => prev ? { ...prev, total_orders: val } : prev);
      await loadData();
      alert("累計訂單數已更新");
    } catch (err: unknown) {
      const msg = err instanceof Error ? err.message : "更新失敗";
      alert(msg);
    } finally {
      setSavingTotalOrders(false);
    }
  };

  const handleResetPassword = async () => {
    if (!editModal) return;
    if (!resetPwd || resetPwd.length < 6) {
      setResetError("密碼至少 6 位");
      return;
    }
    if (resetPwd !== resetConfirmPwd) {
      setResetError("兩次密碼不一致");
      return;
    }

    setResetSaving(true);
    setResetError("");
    setResetSuccess(false);

    try {
      const session = await supabase.auth.getSession();
      const token = session.data.session?.access_token;
      if (!token) {
        setResetError("管理員登入憑證已過期，請重新登入");
        setResetSaving(false);
        return;
      }

      const response = await fetch(
        `${import.meta.env.VITE_PUBLIC_SUPABASE_URL}/functions/v1/reset-password`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            "Authorization": `Bearer ${token}`,
          },
          body: JSON.stringify({ userId: editModal.id, newPassword: resetPwd }),
        }
      );

      const result = await response.json().catch(() => ({
        error: response.status >= 500 ? "伺服器錯誤，請稍後再試" : "重置失敗",
      }));

      if (!response.ok) {
        setResetError(result?.error || "重置密碼失敗，請稍後再試");
        setResetSaving(false);
        return;
      }

      setResetSuccess(true);
      setResetPwd("");
      setResetConfirmPwd("");
      setTimeout(() => setResetSuccess(false), 3000);
    } catch (err: unknown) {
      setResetError(err instanceof Error ? err.message : "重置密碼失敗");
    } finally {
      setResetSaving(false);
    }
  };

  const normalizeEmail = (raw: string): string => {
    const trimmed = raw.trim();
    if (trimmed.includes("@") && !trimmed.includes(".")) {
      return trimmed + ".com";
    }
    return trimmed;
  };

  const handleUpdateEmail = async () => {
    if (!editModal) return;

    const normalizedNewEmail = normalizeEmail(newEmail);
    const normalizedConfirmEmail = normalizeEmail(confirmNewEmail);

    if (!normalizedNewEmail) {
      setEmailError("請輸入新郵箱地址");
      return;
    }
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(normalizedNewEmail)) {
      setEmailError("郵箱格式不正確");
      return;
    }
    if (normalizedNewEmail === editModal.email) {
      setEmailError("新郵箱與目前郵箱相同，無需更改");
      return;
    }
    if (normalizedNewEmail !== normalizedConfirmEmail) {
      setEmailError("兩次輸入的郵箱不一致");
      return;
    }

    setEmailSaving(true);
    setEmailError("");
    setEmailSuccess(false);

    try {
      const session = await supabase.auth.getSession();
      const token = session.data.session?.access_token;
      if (!token) {
        setEmailError("管理員登入憑證已過期，請重新登入");
        setEmailSaving(false);
        return;
      }

      const response = await fetch(
        `${import.meta.env.VITE_PUBLIC_SUPABASE_URL}/functions/v1/admin-update-email`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            "Authorization": `Bearer ${token}`,
          },
          body: JSON.stringify({ userId: editModal.id, newEmail: normalizedNewEmail }),
        }
      );

      const result = await response.json().catch(() => ({
        error: response.status >= 500 ? "伺服器錯誤，請稍後再試" : "更新失敗",
      }));

      if (!response.ok) {
        setEmailError(result?.error || "更新郵箱失敗，請稍後再試");
        setEmailSaving(false);
        return;
      }

      setEmailSuccess(true);
      setNewEmail("");
      setConfirmNewEmail("");
      await loadData();
      setEditModal((prev) => prev ? { ...prev, email: result.newEmail || normalizedNewEmail } : prev);
      setTimeout(() => setEmailSuccess(false), 3000);
    } catch (err: unknown) {
      setEmailError(err instanceof Error ? err.message : "更新郵箱失敗");
    } finally {
      setEmailSaving(false);
    }
  };

  const handleAdjustBalance = async () => {
    if (!editModal || !adjustAmount || adjustSaving) return;

    setAdjustSaving(true);
    setAdjustError("");

    const validation = validateMoneyInput(adjustAmount);
    if (!validation.ok) {
      setAdjustError(validation.error);
      setAdjustSaving(false);
      return;
    }
    const numAmount = validation.value;

    const MAX_SINGLE_ADJUST = 999999999999;
    if (numAmount > MAX_SINGLE_ADJUST) {
      setAdjustError(`單次調整金額上限為 $${MAX_SINGLE_ADJUST.toLocaleString()}，請減少金額`);
      setAdjustSaving(false);
      return;
    }

    const currentBalance = toMoney(editModal.balance);

    if (!Number.isFinite(currentBalance) || currentBalance < 0 || currentBalance > MAX_SINGLE_ADJUST) {
      setAdjustError("目前餘額數值異常，操作已中止，請聯繫技術人員");
      setAdjustSaving(false);
      return;
    }

    const delta = adjustType === "add" ? numAmount : -numAmount;
    const afterBalance = currentBalance + delta;

    if (adjustType === "subtract" && afterBalance < 0) {
      setAdjustError("扣除後餘額不可為負數，請減少扣除金額");
      setAdjustSaving(false);
      return;
    }

    if (!Number.isFinite(afterBalance) || afterBalance < 0 || afterBalance > MAX_SINGLE_ADJUST) {
      setAdjustError("計算後餘額異常，操作已中止，請檢查輸入金額");
      setAdjustSaving(false);
      return;
    }

    try {
      await adjustBalance(editModal.id, delta, adjustNote || undefined);
      await loadData();
      setAdjustAmount("");
      setAdjustNote("");
      setAdjustType("add");
      setAdjustError("");
      closeEditModal();
    } catch (err: unknown) {
      setAdjustError(err instanceof Error ? err.message : "調整失敗");
    } finally {
      setAdjustSaving(false);
    }
  };

  if (!user || !isAdmin) {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center">
        <div className="text-center py-20">
          <div className="w-16 h-16 mx-auto mb-4 flex items-center justify-center">
            <i className="ri-forbid-line text-4xl text-gray-300"></i>
          </div>
          <p className="text-gray-500 text-lg mb-4">權限不足</p>
          <p className="text-sm text-gray-400 mb-6">只有管理員才能訪問此頁面</p>
          <button onClick={() => navigate("/")} className="px-6 py-2.5 bg-amber-500 text-white rounded-full text-sm font-medium cursor-pointer">返回首頁</button>
        </div>
      </div>
    );
  }

  const filtered = users.filter((u) => {
    const matchSearch =
      search === "" ||
      u.name.includes(search) ||
      u.email.includes(search) ||
      (u.phone || "").includes(search);
    const matchLevel = filterLevel === "all" || u.level === filterLevel;
    return matchSearch && matchLevel;
  });

  const stats = {
    total: users.length,
    normal: users.filter((u) => u.level === "普通分銷").length,
    core: users.filter((u) => u.level === "核心分銷").length,
    dealer: users.filter((u) => u.level === "特約經銷").length,
    admin: users.filter((u) => u.level === "管理員").length,
    totalBalance: users.reduce((sum, u) => sum + toMoney(u.balance), 0),
  };

  return (
    <AdminLayout>
      <div className="max-w-7xl mx-auto space-y-6">
        <div className="mb-6">
          <h1 className="text-xl font-bold text-gray-900">會員管理</h1>
          <p className="text-sm text-gray-500 mt-0.5">
            查看所有分銷商資料，並手動調整錢包餘額與會員等級
          </p>
        </div>

        {loadError && (
          <div className="bg-red-50 border border-red-200 rounded-xl p-4 mb-6 flex items-start gap-3 shadow-sm">
            <div className="w-5 h-5 flex items-center justify-center flex-shrink-0 mt-0.5">
              <i className="ri-error-warning-line text-red-500 text-sm"></i>
            </div>
            <div>
              <p className="text-sm font-medium text-red-600">載入失敗</p>
              <p className="text-xs text-red-500 mt-0.5">{loadError}</p>
              <button
                onClick={loadData}
                className="mt-2 text-xs text-amber-600 hover:text-amber-700 font-medium cursor-pointer"
              >
                點擊重新載入
              </button>
            </div>
          </div>
        )}

        <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
          {[
            { label: "總會員", value: stats.total, color: "bg-gray-100 text-gray-700" },
            { label: "普通分銷", value: stats.normal, color: "bg-slate-100 text-slate-700" },
            { label: "核心分銷", value: stats.core, color: "bg-amber-100 text-amber-700" },
            { label: "特約經銷", value: stats.dealer, color: "bg-emerald-100 text-emerald-700" },
            { label: "總餘額", value: `$${stats.totalBalance.toLocaleString()}`, color: "bg-blue-100 text-blue-700" },
          ].map((s) => (
            <div
              key={s.label}
              className={`rounded-xl p-4 text-center shadow-sm ${s.color}`}
            >
              <p className="text-2xl font-bold">{s.value}</p>
              <p className="text-xs mt-1">{s.label}</p>
            </div>
          ))}
        </div>

        <div className="bg-white rounded-xl border border-gray-100 shadow-sm p-4">
          <div className="flex flex-col sm:flex-row gap-3">
            <div className="relative flex-1">
              <div className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 flex items-center justify-center">
                <i className="ri-search-line text-sm text-gray-400"></i>
              </div>
              <input
                type="text"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="搜尋姓名、Email 或電話..."
                className="w-full pl-9 pr-3 py-2.5 rounded-lg border border-gray-200 text-sm outline-none focus:border-amber-400"
              />
            </div>
            <div className="flex gap-2 flex-wrap">
              {[
                { label: "全部", value: "all" },
                { label: "普通分銷", value: "普通分銷" },
                { label: "核心分銷", value: "核心分銷" },
                { label: "特約經銷", value: "特約經銷" }
              ].map((f) => (
                <button
                  key={f.value}
                  onClick={() => setFilterLevel(f.value)}
                  className={`px-4 py-2 rounded-lg text-sm font-medium transition-all cursor-pointer whitespace-nowrap ${
                    filterLevel === f.value
                      ? "bg-amber-500 text-white"
                      : "bg-gray-100 text-gray-700 hover:bg-gray-200"
                  }`}
                >
                  {f.label}
                </button>
              ))}
            </div>
          </div>
        </div>

        <div className="bg-white rounded-xl border border-gray-100 shadow-sm overflow-hidden">
          {isLoading ? (
            <div className="text-center py-12 text-gray-400 text-sm">載入中...</div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="bg-gray-50 border-b border-gray-100">
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500">會員</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500">聯繫方式</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500">等級</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500">錢包餘額</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500">累計訂單</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500">審核中</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500">註冊日期</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500">操作</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-50">
                  {filtered.map((u) => (
                    <tr
                      key={u.id}
                      className="hover:bg-gray-50/50 cursor-pointer"
                      onClick={() => openEditModal(u)}
                    >
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-3">
                          {u.avatar ? (
                            <img
                              src={u.avatar}
                              alt={u.name}
                              className="w-9 h-9 rounded-full object-cover"
                            />
                          ) : (
                            <div className="w-9 h-9 rounded-full bg-amber-100 flex items-center justify-center">
                              <i className="ri-user-line text-amber-600 text-sm"></i>
                            </div>
                          )}
                          <div>
                            <p className="font-medium text-gray-800">{u.name}</p>
                            <p className="text-xs text-gray-400">{u.email}</p>
                            {u.notes && (
                              <p className="text-xs text-amber-600 mt-0.5 line-clamp-1">{u.notes}</p>
                            )}
                          </div>
                        </div>
                      </td>
                      <td className="px-4 py-3">
                        <p className="text-gray-700">{u.phone || "-"}</p>
                      </td>
                      <td className="px-4 py-3">
                        <span
                          className={`text-xs px-2.5 py-0.5 rounded-full font-medium ${
                            LEVEL_STYLES[u.level] || "bg-gray-100 text-gray-600"
                          }`}
                        >
                          {u.level}
                        </span>
                      </td>
                      <td className="px-4 py-3">
                        <p className="font-bold text-amber-600">
                          ${toMoney(u.balance).toLocaleString()}
                        </p>
                      </td>
                      <td className="px-4 py-3">
                        <span className="text-sm font-semibold text-sky-600">
                          {(u.total_orders ?? 0).toLocaleString()} 件
                        </span>
                      </td>
                      <td className="px-4 py-3">
                        <p className="text-gray-600">
                          {toMoney(u.pending_balance) > 0 ? (
                            <span className="text-amber-600">
                              ${toMoney(u.pending_balance).toLocaleString()}
                            </span>
                          ) : (
                            <span className="text-gray-400">-</span>
                          )}
                        </p>
                      </td>
                      <td className="px-4 py-3 text-xs text-gray-400">
                        {new Date(u.created_at).toLocaleDateString("zh-TW")}
                      </td>
                      <td className="px-4 py-3">
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            openEditModal(u);
                          }}
                          className="px-3 py-1.5 rounded-lg bg-amber-500 text-white text-xs font-medium hover:bg-amber-600 transition-all cursor-pointer flex items-center gap-1.5 whitespace-nowrap"
                        >
                          <div className="w-3.5 h-3.5 flex items-center justify-center">
                            <i className="ri-edit-line text-[10px]"></i>
                          </div>
                          調整
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
          {!isLoading && filtered.length === 0 && (
            <div className="text-center py-12 text-gray-400 text-sm">
              暫無符合條件的會員
            </div>
          )}
        </div>
      </div>

      {editModal && (
        <div className="fixed inset-0 z-50 bg-black/60 flex items-center justify-center p-4">
          <div className="bg-white rounded-xl p-6 w-full max-w-lg max-h-[90vh] overflow-y-auto">
            <div className="flex items-center gap-3 mb-5">
              {editModal.avatar ? (
                <img src={editModal.avatar} alt={editModal.name} className="w-12 h-12 rounded-full object-cover" />
              ) : (
                <div className="w-12 h-12 rounded-full bg-amber-100 flex items-center justify-center">
                  <i className="ri-user-line text-amber-600 text-lg"></i>
                </div>
              )}
              <div>
                <h3 className="text-lg font-bold text-gray-900">{editModal.name}</h3>
                <p className="text-xs text-gray-500">{editModal.email} · 當前餘額 <span className="font-bold text-amber-600">${toMoney(editModal.balance).toLocaleString()}</span> · 累計訂單 <span className="font-bold text-sky-600">{(editModal.total_orders ?? 0).toLocaleString()} 件</span></p>
              </div>
            </div>

            <div className="space-y-5">
              <div className="bg-gray-50 rounded-xl p-4 space-y-4">
                <div className="flex items-center gap-2 mb-1">
                  <div className="w-5 h-5 flex items-center justify-center">
                    <i className="ri-user-settings-line text-sm text-gray-500"></i>
                  </div>
                  <h4 className="text-sm font-semibold text-gray-800">會員資料</h4>
                </div>

                <div>
                  <label className="text-sm text-gray-600 mb-1.5 block">會員等級</label>
                  <div className="relative">
                    <select
                      value={editLevel}
                      onChange={(e) => setEditLevel(e.target.value)}
                      className="w-full px-3 py-2.5 rounded-lg border border-gray-200 text-sm outline-none focus:border-amber-400 appearance-none bg-white"
                    >
                      {DISTRIBUTOR_LEVELS.map((level) => (
                        <option key={level} value={level}>{level}</option>
                      ))}
                    </select>
                    <div className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 flex items-center justify-center pointer-events-none">
                      <i className="ri-arrow-down-s-line text-gray-400 text-xs"></i>
                    </div>
                  </div>
                  <p className="text-xs text-gray-400 mt-1">
                    等級決定認購價格：普通分銷為基準價，核心分銷與特約經銷享有更優惠的認購價
                  </p>
                </div>

                <div>
                  <label className="text-sm text-gray-600 mb-1.5 block">聯繫方式（手機號碼）</label>
                  <input
                    type="tel"
                    value={editPhone}
                    onChange={(e) => setEditPhone(e.target.value)}
                    placeholder="例如：0912-345-678"
                    className="w-full px-3 py-2.5 rounded-lg border border-gray-200 text-sm outline-none focus:border-amber-400"
                  />
                </div>

                <div>
                  <label className="text-sm text-gray-600 mb-1.5 block">備註</label>
                  <textarea
                    value={editNotes}
                    onChange={(e) => setEditNotes(e.target.value)}
                    placeholder="記錄該客戶的特殊狀況，例如：偏好LINE聯繫、長期合作夥伴..."
                    rows={3}
                    maxLength={200}
                    className="w-full px-3 py-2.5 rounded-lg border border-gray-200 text-sm outline-none focus:border-amber-400 resize-none"
                  />
                  <p className="text-xs text-gray-400 text-right mt-1">{editNotes.length}/200</p>
                </div>

                <div className="flex items-center gap-3">
                  <button
                    onClick={handleSaveProfile}
                    disabled={saveStatus === "saving"}
                    className="px-4 py-2 rounded-lg bg-amber-500 text-white text-sm font-medium hover:bg-amber-600 transition-all disabled:opacity-60 cursor-pointer whitespace-nowrap"
                  >
                    {saveStatus === "saving" ? (
                      <span className="flex items-center gap-1.5">
                        <div className="w-3.5 h-3.5 flex items-center justify-center animate-spin">
                          <i className="ri-loader-2-line text-xs"></i>
                        </div>
                        儲存中...
                      </span>
                    ) : saveStatus === "success" ? (
                      <span className="flex items-center gap-1.5">
                        <div className="w-3.5 h-3.5 flex items-center justify-center">
                          <i className="ri-check-line text-xs"></i>
                        </div>
                        已儲存
                      </span>
                    ) : (
                      "儲存資料變更"
                    )}
                  </button>
                  {saveStatus === "error" && (
                    <span className="text-xs text-red-500">儲存失敗，請重試</span>
                  )}
                </div>
              </div>

              <div className="bg-gray-50 rounded-xl p-4 space-y-4">
                <div className="flex items-center gap-2 mb-1">
                  <div className="w-5 h-5 flex items-center justify-center">
                    <i className="ri-bar-chart-grouped-line text-sm text-gray-500"></i>
                  </div>
                  <h4 className="text-sm font-semibold text-gray-800">累計訂單數</h4>
                </div>
                <p className="text-xs text-gray-400">此數值為前台顯示的累計訂單數，可直接手動修改</p>
                <div className="flex items-center gap-3">
                  <input
                    type="number"
                    min={0}
                    value={editTotalOrders}
                    onChange={(e) => setEditTotalOrders(e.target.value)}
                    className="flex-1 px-3 py-2.5 rounded-lg border border-gray-200 text-sm outline-none focus:border-amber-400"
                  />
                  <span className="text-sm text-gray-500">件</span>
                  <button
                    onClick={handleSaveTotalOrders}
                    disabled={savingTotalOrders}
                    className="px-4 py-2.5 rounded-lg bg-sky-600 text-white text-sm font-medium hover:bg-sky-700 transition-all disabled:opacity-50 cursor-pointer whitespace-nowrap"
                  >
                    {savingTotalOrders ? (
                      <span className="flex items-center gap-1.5">
                        <div className="w-3.5 h-3.5 flex items-center justify-center animate-spin">
                          <i className="ri-loader-2-line text-xs"></i>
                        </div>
                        儲存中...
                      </span>
                    ) : (
                      "更新"
                    )}
                  </button>
                </div>
              </div>

              <div className="bg-gray-50 rounded-xl p-4 space-y-4">
                <div className="flex items-center gap-2 mb-1">
                  <div className="w-5 h-5 flex items-center justify-center">
                    <i className="ri-mail-send-line text-sm text-gray-500"></i>
                  </div>
                  <h4 className="text-sm font-semibold text-gray-800">更改登入郵箱</h4>
                </div>
                <p className="text-xs text-gray-400 mb-1">僅管理員可見，更改後該會員需使用新郵箱登入。系統會同步更新 Auth 系統與資料庫的郵箱記錄。</p>

                <div>
                  <label className="text-sm text-gray-600 mb-1.5 block">目前郵箱</label>
                  <input
                    type="text"
                    value={editModal.email}
                    disabled
                    className="w-full px-3 py-2.5 rounded-lg border border-gray-200 text-sm bg-gray-100 text-gray-500 cursor-not-allowed"
                  />
                </div>

                <div>
                  <label className="text-sm text-gray-600 mb-1.5 block">新郵箱地址</label>
                  <input
                    type="email"
                    value={newEmail}
                    onChange={(e) => { setNewEmail(e.target.value); setEmailError(""); }}
                    placeholder="例如：newemail@example.com"
                    className="w-full px-3 py-2.5 rounded-lg border border-gray-200 text-sm outline-none focus:border-amber-400"
                  />
                </div>

                <div>
                  <label className="text-sm text-gray-600 mb-1.5 block">確認新郵箱地址</label>
                  <input
                    type="email"
                    value={confirmNewEmail}
                    onChange={(e) => { setConfirmNewEmail(e.target.value); setEmailError(""); }}
                    placeholder="再次輸入新郵箱地址"
                    className="w-full px-3 py-2.5 rounded-lg border border-gray-200 text-sm outline-none focus:border-amber-400"
                  />
                </div>

                {emailError && (
                  <div className="bg-red-50 border border-red-200 rounded-lg px-3 py-2 text-xs text-red-600 flex items-center gap-2">
                    <div className="w-4 h-4 flex items-center justify-center flex-shrink-0">
                      <i className="ri-error-warning-line text-xs"></i>
                    </div>
                    {emailError}
                  </div>
                )}
                {emailSuccess && (
                  <div className="bg-emerald-50 border border-emerald-200 rounded-lg px-3 py-2 text-xs text-emerald-600 flex items-center gap-2">
                    <div className="w-4 h-4 flex items-center justify-center flex-shrink-0">
                      <i className="ri-check-line text-xs"></i>
                    </div>
                    郵箱更新成功！該會員下次登入需使用新郵箱地址
                  </div>
                )}

                <button
                  onClick={handleUpdateEmail}
                  disabled={emailSaving || !newEmail || !confirmNewEmail}
                  className="w-full py-2.5 rounded-lg bg-sky-600 text-white text-sm font-medium hover:bg-sky-700 transition-all disabled:opacity-50 cursor-pointer whitespace-nowrap"
                >
                  {emailSaving ? (
                    <span className="flex items-center justify-center gap-1.5">
                      <div className="w-4 h-4 flex items-center justify-center animate-spin">
                        <i className="ri-loader-2-line text-xs"></i>
                      </div>
                      處理中...
                    </span>
                  ) : (
                    "確認更改登入郵箱"
                  )}
                </button>
              </div>

              <div className="bg-gray-50 rounded-xl p-4 space-y-4">
                <div className="flex items-center gap-2 mb-1">
                  <div className="w-5 h-5 flex items-center justify-center">
                    <i className="ri-lock-password-line text-sm text-gray-500"></i>
                  </div>
                  <h4 className="text-sm font-semibold text-gray-800">重置密碼</h4>
                </div>
                <p className="text-xs text-gray-400 mb-3">僅管理員可見，輸入新密碼後將立即覆蓋該會員的登入密碼</p>

                <div className="space-y-3">
                  <div>
                    <label className="text-sm text-gray-600 mb-1.5 block">新密碼</label>
                    <input
                      type="password"
                      value={resetPwd}
                      onChange={(e) => { setResetPwd(e.target.value); setResetError(""); }}
                      placeholder="至少 6 位字元"
                      className="w-full px-3 py-2.5 rounded-lg border border-gray-200 text-sm outline-none focus:border-amber-400"
                    />
                  </div>
                  <div>
                    <label className="text-sm text-gray-600 mb-1.5 block">確認新密碼</label>
                    <input
                      type="password"
                      value={resetConfirmPwd}
                      onChange={(e) => { setResetConfirmPwd(e.target.value); setResetError(""); }}
                      placeholder="再次輸入新密碼"
                      className="w-full px-3 py-2.5 rounded-lg border border-gray-200 text-sm outline-none focus:border-amber-400"
                    />
                  </div>
                </div>

                {resetError && (
                  <div className="bg-red-50 border border-red-200 rounded-lg px-3 py-2 text-xs text-red-600 flex items-center gap-2 mt-3">
                    <div className="w-4 h-4 flex items-center justify-center flex-shrink-0">
                      <i className="ri-error-warning-line text-xs"></i>
                    </div>
                    {resetError}
                  </div>
                )}
                {resetSuccess && (
                  <div className="bg-emerald-50 border border-emerald-200 rounded-lg px-3 py-2 text-xs text-emerald-600 flex items-center gap-2 mt-3">
                    <div className="w-4 h-4 flex items-center justify-center flex-shrink-0">
                      <i className="ri-check-line text-xs"></i>
                    </div>
                    密碼重置成功，該會員下次登入需使用新密碼
                  </div>
                )}

                <button
                  onClick={handleResetPassword}
                  disabled={resetSaving || !resetPwd || !resetConfirmPwd}
                  className="w-full py-2.5 rounded-lg bg-stone-700 text-white text-sm font-medium hover:bg-stone-800 transition-all disabled:opacity-50 cursor-pointer whitespace-nowrap"
                >
                  {resetSaving ? (
                    <span className="flex items-center justify-center gap-1.5">
                      <div className="w-4 h-4 flex items-center justify-center animate-spin">
                        <i className="ri-loader-2-line text-xs"></i>
                      </div>
                      處理中...
                    </span>
                  ) : (
                    "確認重置密碼"
                  )}
                </button>
              </div>

              <div className="bg-gray-50 rounded-xl p-4 space-y-4">
                <div className="flex items-center gap-2 mb-1">
                  <div className="w-5 h-5 flex items-center justify-center">
                    <i className="ri-coins-line text-sm text-gray-500"></i>
                  </div>
                  <h4 className="text-sm font-semibold text-gray-800">調整餘額</h4>
                </div>

                <div>
                  <label className="text-sm text-gray-600 mb-1.5 block">操作類型</label>
                  <div className="flex gap-2">
                    <button
                      onClick={() => { setAdjustType("add"); setAdjustError(""); }}
                      className={`flex-1 py-2.5 rounded-lg text-sm font-medium transition-all cursor-pointer ${
                        adjustType === "add"
                          ? "bg-emerald-500 text-white"
                          : "bg-white border border-gray-200 text-gray-700 hover:bg-gray-100"
                      }`}
                    >
                      <div className="w-4 h-4 flex items-center justify-center mx-auto mb-0.5">
                        <i className="ri-add-line text-xs"></i>
                      </div>
                      增加餘額
                    </button>
                    <button
                      onClick={() => { setAdjustType("subtract"); setAdjustError(""); }}
                      className={`flex-1 py-2.5 rounded-lg text-sm font-medium transition-all cursor-pointer ${
                        adjustType === "subtract"
                          ? "bg-red-500 text-white"
                          : "bg-white border border-gray-200 text-gray-700 hover:bg-gray-100"
                      }`}
                    >
                      <div className="w-4 h-4 flex items-center justify-center mx-auto mb-0.5">
                        <i className="ri-subtract-line text-xs"></i>
                      </div>
                      扣除餘額
                    </button>
                  </div>
                </div>

                <div>
                  <label className="text-sm text-gray-600 mb-1.5 block">金額（$）<span className="text-red-400">*</span></label>
                  <input
                    type="text"
                    inputMode="numeric"
                    value={adjustAmount}
                    onChange={(e) => {
                      const raw = e.target.value;
                      if (raw === "") {
                        setAdjustAmount("");
                        setAdjustError("");
                        return;
                      }
                      const clean = raw.replace(/[^0-9]/g, "").slice(0, 12);
                      setAdjustAmount(clean);
                      setAdjustError("");
                    }}
                    placeholder="輸入整數金額，例如：1000"
                    className="w-full px-3 py-2.5 rounded-lg border border-gray-200 text-sm outline-none focus:border-amber-400"
                  />
                  <p className="text-xs text-gray-400 mt-1">金額必須為正整數，不可含小數點</p>
                  {adjustType === "subtract" && adjustAmount && (
                    (() => {
                      const current = toMoney(editModal.balance);
                      const amount = toMoney(adjustAmount);
                      const after = current - amount;
                      const isValid = Number.isFinite(after) && after >= 0 && after <= 999999999999;
                      if (!isValid) {
                        return (
                          <p className="text-xs text-red-600 mt-1 font-medium">
                            扣除後餘額計算異常，請檢查輸入金額
                          </p>
                        );
                      }
                      return (
                        <p className="text-xs text-red-500 mt-1">
                          扣除後餘額：${after.toLocaleString()}
                        </p>
                      );
                    })()
                  )}
                  {adjustType === "add" && adjustAmount && (
                    (() => {
                      const current = toMoney(editModal.balance);
                      const amount = toMoney(adjustAmount);
                      const after = current + amount;
                      const isValid = Number.isFinite(after) && after >= 0 && after <= 999999999999;
                      if (!isValid) {
                        return (
                          <p className="text-xs text-red-600 mt-1 font-medium">
                            增加後餘額計算異常，請檢查輸入金額
                          </p>
                        );
                      }
                      return (
                        <p className="text-xs text-emerald-600 mt-1">
                          增加後餘額：${after.toLocaleString()}
                        </p>
                      );
                    })()
                  )}
                </div>

                <div>
                  <label className="text-sm text-gray-600 mb-1.5 block">備註（餘額調整）</label>
                  <input
                    type="text"
                    value={adjustNote}
                    onChange={(e) => setAdjustNote(e.target.value)}
                    placeholder="例如：LINE 確認收款、銀行轉帳入帳..."
                    className="w-full px-3 py-2.5 rounded-lg border border-gray-200 text-sm outline-none focus:border-amber-400"
                  />
                </div>

                {adjustError && (
                  <div className="bg-red-50 border border-red-200 rounded-lg px-3 py-2 text-xs text-red-600 flex items-center gap-2">
                    <div className="w-4 h-4 flex items-center justify-center flex-shrink-0">
                      <i className="ri-error-warning-line text-xs"></i>
                    </div>
                    {adjustError}
                  </div>
                )}

                <button
                  onClick={handleAdjustBalance}
                  disabled={adjustAmount === "" || toMoney(adjustAmount) <= 0 || adjustError !== "" || adjustSaving}
                  className={`w-full py-2.5 rounded-lg text-white text-sm font-medium transition-all disabled:opacity-50 cursor-pointer ${
                    adjustType === "add"
                      ? "bg-emerald-500 hover:bg-emerald-600"
                      : "bg-red-500 hover:bg-red-600"
                  }`}
                >
                  {adjustSaving ? (
                    <span className="flex items-center justify-center gap-1.5">
                      <div className="w-4 h-4 flex items-center justify-center animate-spin">
                        <i className="ri-loader-2-line text-xs"></i>
                      </div>
                      處理中...
                    </span>
                  ) : (
                    `確認${adjustType === "add" ? "增加" : "扣除"}`
                  )}
                </button>
              </div>
            </div>

            <div className="mt-6 pt-4 border-t border-gray-100">
              <button
                onClick={closeEditModal}
                className="w-full py-2.5 rounded-lg border border-gray-200 text-gray-700 text-sm font-medium hover:bg-gray-50 cursor-pointer"
              >
                關閉視窗
              </button>
            </div>
          </div>
        </div>
      )}
    </AdminLayout>
  );
}